import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";

// How long a first choice shows as a selected card before the cards fold.
const FOLD_DELAY_MS = 140;

// The care segments editor, drawn in the page. Its fields are the page's
// own, submitted with it; this only redraws its controls as they change.
export default class extends Controller {
  static targets = ["dateInputs", "mode", "modes", "summary", "timeControl"];

  connect() {
    this.requestSequence = 0;
    this.transitionSequence = 0;
    this.scrollErrorsIntoView();
  }

  static values = {
    draftUrl: String,
    editorId: String,
    fieldNamePrefix: String,
    bookingRequestId: String,
    timeZone: String
  };

  preview(event) {
    const changedField = event.params.changedField || "ends_at";
    const params = { changed_field: changedField };
    const input = event.currentTarget;

    if (input.name) params[input.name] = input.value;

    this.request(this.draftUrlValue, params, { scrollToNextStep: true });
  }

  // A first choice folds the mode cards into a segmented control; a later
  // one slides the selected segment across. The heading and layout follow
  // the modes target's chosen mode (care-segment-editor.scss), set inside
  // the transition. The dates and times for the mode are fetched at once,
  // but drawn only after the transition, so they don't land in its snapshot.
  // After the fold they rise in (data-entering); after a switch they look
  // the same as before, so they're swapped in place.
  chooseMode(event) {
    const mode = event.currentTarget.value;
    const firstChoice = !this.modesTarget.dataset.chosenMode;
    if (!firstChoice) delete this.modesTarget.dataset.entering;

    const transitioned = this.transition(firstChoice ? "fold" : "switch", () => {
      this.modesTarget.dataset.chosenMode = mode;
    }, { delay: firstChoice ? FOLD_DELAY_MS : 0 });

    this.request(this.draftUrlValue, { changed_field: "mode", [event.currentTarget.name]: mode }, {
      renderAfter: transitioned,
      beforeRender: firstChoice ? () => { this.modesTarget.dataset.entering = ""; } : undefined
    });
  }

  // Runs update inside a view transition named kind, which scopes its
  // styles. Without view transitions, or with reduced motion, it just runs.
  // A later choice made during the delay replaces this one.
  async transition(kind, update, { delay = 0 } = {}) {
    const sequence = ++this.transitionSequence;
    if (typeof document.startViewTransition !== "function" || this.prefersReducedMotion()) {
      update();
      return;
    }

    if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
    if (sequence !== this.transitionSequence) return;

    const root = document.documentElement;
    root.dataset.careSegmentsMorph = kind;
    try {
      await document.startViewTransition(update).finished;
    } catch {
      // A skipped transition still applied its update.
    } finally {
      if (sequence === this.transitionSequence) delete root.dataset.careSegmentsMorph;
    }
  }

  // The times show once dates are chosen.
  datesChanged(event) {
    this.setDraftDates(event.detail.selectedDates || []);
    this.request(this.draftUrlValue, { changed_field: "dates" });
  }

  async request(url, extraParams = {}, options = {}) {
    const sequence = ++this.requestSequence;
    const body = this.formData(extraParams);

    const response = await fetch(url, {
      method: "POST",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": this.csrfToken()
      },
      body
    });

    const html = await response.text();
    if (options.renderAfter) await options.renderAfter;
    if (html && sequence === this.requestSequence) {
      options.beforeRender?.();
      Turbo.renderStreamMessage(html);
      if (options.scrollToNextStep) {
        requestAnimationFrame(() => this.scrollToNextStep());
      }
    }

    return response;
  }

  // Brings the next thing the owner needs to act on into view: the first
  // unfilled time control, or the selection summary once every time is chosen.
  scrollToNextStep() {
    const target =
      this.firstUnfilledTimeControl() ||
      (this.hasSummaryTarget ? this.summaryTarget : null);

    this.scrollStepIntoView(target);
  }

  firstUnfilledTimeControl() {
    return this.timeControlTargets.find((control) => {
      const radios = control.querySelectorAll('input[type="radio"]');

      return radios.length > 0 && ![...radios].some((radio) => radio.checked);
    });
  }

  scrollStepIntoView(element) {
    if (!element) return;

    const target = element.closest("section") || element;
    target.scrollIntoView({
      behavior: this.prefersReducedMotion() ? "auto" : "smooth",
      block: "start"
    });
  }

  // A failed submit draws the page again, with the step's errors in the
  // editor's alert. The first editor with errors brings them into view.
  scrollErrorsIntoView() {
    const alert = this.element.querySelector(`#${this.editorIdValue}_errors`);
    if (!alert || document.querySelector('.alert[id$="_errors"]') !== alert) return;

    requestAnimationFrame(() => {
      const { top, bottom } = alert.getBoundingClientRect();
      if (top < 0 || bottom > window.innerHeight) alert.scrollIntoView({ block: "center" });
    });
  }

  formData(extraParams = {}) {
    const form = this.element.closest("form");
    const body = form ? new FormData(form) : new FormData();

    body.delete("_method");
    body.set("editor_id", this.editorIdValue);
    body.set("field_name_prefix", this.fieldNamePrefixValue);
    if (this.hasBookingRequestIdValue) {
      body.set("booking_request_id", this.bookingRequestIdValue);
    }
    if (this.hasTimeZoneValue) {
      body.set("time_zone", this.timeZoneValue);
    }
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) body.set(key, value);
    });

    return body;
  }

  setDraftDates(selectedDates) {
    const dates = [...selectedDates].filter(Boolean).sort();
    if (!this.hasDateInputsTarget) return;

    const mode = this.modeTargets.find((input) => input.checked)?.value;

    this.dateInputsTarget.replaceChildren(...this.draftDateInputs(mode, dates));
  }

  // The editor's one care set is row 0 of the schedule's fields.
  draftDateInputs(mode, dates) {
    const prefix = `${this.fieldNamePrefixValue}[0]`;

    if (mode === "daily") {
      return dates.map((date) => this.hiddenInput(`${prefix}[dates][]`, date));
    }

    if (mode === "continuous") {
      return [
        dates[0] && this.hiddenInput(`${prefix}[starts_on]`, dates[0]),
        dates.length > 1 && this.hiddenInput(`${prefix}[ends_on]`, dates.at(-1))
      ].filter(Boolean);
    }

    return [];
  }

  hiddenInput(name, value) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    return input;
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }
}
